import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import classnames from "classnames";
import dayjs from "dayjs";

import {TERM_CHOICES, TERM_VALUES} from "shared/constants/terms";
import {AMOUNT_VALUES, PAYMENT_CHOICES, UI_PAYMENT_VALUES} from "shared/constants/payment";
import {HEALTH_SUBJECT_CHOICES, HEALTH_VALUES_FOR_SMOKERS} from "shared/constants/health";
import {GENDER_CHOICES} from "shared/constants/gender";

import {applyProduct} from "shared/networking/quotes";
import Slider from "shared/components/Slider";
import ButtonInput from "shared/components/ButtonInput";
import Hint from "shared/components/Hint";
import SpinnerOverlay from "shared/components/SpinnerOverlay";
import RadioButtonList from "shared/components/RadioButtonList";

import useNavigate from "apps/QuoterApp/hooks/useNavigate";

import {selectProduct, showProductDetails} from "apps/QuoterApp/reduxElements/slices/quoteRequest";
import {openApplyModal} from "apps/QuoterApp/reduxElements/slices/showApplyModal";
import useSetQuoteInput from "apps/QuoterApp/hooks/useSetQuoteInput";

import QuoterLayout from "apps/QuoterApp/components/QuoterLayout";
import ContactInfo from "apps/QuoterApp/components/ContactInfo";

import ProductItem from "./ProductItem";
import ProductSummary from "./ProductSummary";

import './styles.scss';
import HealthPicker from "../BasicInformationPage/HealthPicker";
import {SMOKER} from "shared/constants/smoking";
import {RATE_CLASSES_BY_HEALTH_VALUE} from "shared/constants/rateClasses";
import {TABLE_RATING_BY_HEALTH_OPTION} from "shared/constants/tableRating";


function QuoteResultPage() {
    const dispatch = useDispatch();

    const history = useHistory();
    const {goToQuoterInput} = useNavigate();

    const {loading, selectedProduct, result} = useSelector(state => state.quoteRequest);
    const validInput = useSelector(state => state.quoteInput.valid);
    const {gender, dob, healthValue} = useSelector(state => state.quoteInput.data);

    const {
        id: selectedProductId,
        premium: selectedPremium,
        term: selectedTerm,
    } = result.products.find(({id}) => id === selectedProduct) || {};
    const {id: quoteRequestId} = result;

    useEffect(() => {
        if (!validInput) {
            goToQuoterInput();
        }
    }, [goToQuoterInput, history, validInput]);

    const onApply = async (item) => {
        dispatch(selectProduct(item.id));

        const urlParams = new URLSearchParams(window.location.search);
        const showClientView = urlParams.get('showClientView') !== '0';
        const allowTickets = urlParams.get('allowTickets') !== '0';
        const applyFull = urlParams.get('applyFull') === '1';

        if (applyFull) {
            try {
                const {applicationUrl} = await applyProduct(
                    quoteRequestId,
                    selectedProductId,
                    selectedPremium,
                    selectedTerm,
                    showClientView,
                    allowTickets,
                );
                window.location.href = applicationUrl;
            } catch (e) {
                // TODO: ALERT USER
            }
        } else {
            if (allowTickets) {
                dispatch(openApplyModal());
            } else {
                try {
                    const {applicationUrl} = await applyProduct(
                        quoteRequestId,
                        selectedProductId,
                        selectedPremium,
                        selectedTerm,
                        showClientView,
                        allowTickets,
                    );
                    window.location.href = applicationUrl;
                } catch (e) {
                    // TODO: ALERT USER
                }
            }
        }
    };

    const onSelect = id => {
        dispatch(selectProduct(id));
    };

    const onDetails = () => {
        dispatch(showProductDetails());
    };

    return <>
        <SpinnerOverlay loading={loading}/>

        <QuoterLayout className={"QuoteResultPage"}>
            <QuoterLayout.LeftBar>
                <ContactInfo className={"QuoteResultPage__ContactInfo__LeftBar"}/>
            </QuoterLayout.LeftBar>

            <QuoterLayout.Content className={"QuoteResultPage__Content"}>
                <SliderSection className={"Content"}/>

                {(loading || result.products.length > 0) && <h1 className={"QuoteResultPage__Title"}>
                    {loading ? 'We are looking for your lowest rates' : 'Here are your lowest rates'}
                </h1>}
                {!loading && result.products.length > 0 && <div className={'QuoteResultPage__Subtitle'}>
                    Results for a {ageFromDob(dob)} year old {GENDER_CHOICES[gender]} with {HEALTH_SUBJECT_CHOICES[healthValue]}.
                </div>}

                {loading
                    ? Array(5).fill(<ProductItem/>)
                    : result.products.map(item =>
                        <ProductItem
                            item={item}
                            onApply={onApply}
                            selected={item.id === selectedProduct}
                            onSelect={onSelect}
                        />,
                    )
                }

                {!loading && result.products.length === 0 && <div>
                    We’re sorry but there are no products available at your selected age and health rating in our
                    system. But don’t worry, we have a variety of other products that are not in the system and if you’d
                    like to review these options with our insurance experts just click the Request Professional
                    Assistance button below.
                </div>}

                <ButtonInput
                    outline={true}
                    className={"QuoteResultPage__BackButton"}
                    onClick={goToQuoterInput}
                >
                    Back
                </ButtonInput>

                <ContactInfo className={"QuoteResultPage__ContactInfo__Content"}/>
            </QuoterLayout.Content>

            <QuoterLayout.RightBar>
                <SliderSection className={"RightBar"}/>
                <ProductSummary
                    item={!loading && result.products.find(({id}) => id === selectedProduct)}
                    onApply={onApply}
                    onDetails={onDetails}
                />
            </QuoterLayout.RightBar>
        </QuoterLayout>
    </>;
}

function SliderSection({className}) {
    const setQuoteInput = useSetQuoteInput();

    const {term, amount, payment, smoking, healthValue} = useSelector(state => state.quoteInput.data);
    const [termHover, setTermHover] = useState();
    const [amountHover, setAmountHover] = useState();

    const setHealthValue = value => {
        setQuoteInput({
            healthValue: value,
            rateClasses: RATE_CLASSES_BY_HEALTH_VALUE[value],
            tableRating: TABLE_RATING_BY_HEALTH_OPTION[value],
        });
    };

    const paymentOptions = UI_PAYMENT_VALUES.map(value => ({value, label: PAYMENT_CHOICES[value]}));

    return <div className={classnames("QuoteResultPage__SliderSection", className)}>

        <div className={"QuoteResultPage__SliderSection__SubSection"}>
            <div className={"QuoteResultPage__SliderSection__Label"}>Which of these describes best your health?</div>
            <HealthPicker smoking={smoking} onChange={setHealthValue} value={healthValue}/>
        </div>

        <div className={"QuoteResultPage__SliderSection__SubSection"}>
            <div className={"QuoteResultPage__SliderSection__Label"}>How much coverage do you need?</div>
            <div className={"QuoteResultPage__SliderSection__Value"}>
                <span style={{fontSize: '0.65em'}}>$</span>
                <span>{(amountHover || amount).toLocaleString('en-US')}</span>
            </div>
            <Slider
                className={"QuoteResultPage__SliderSection__Slider"}
                values={AMOUNT_VALUES}
                defaultValue={amount}
                onChange={amount => setAmountHover(amount)}
                onAfterChange={amount => {
                    setAmountHover(amount);
                    setQuoteInput({amount});
                }}
            />
            <Hint>
                Recommended rule of thumb is to have <b>10x - 15x your Annual Income</b>
            </Hint>
        </div>

        <div className={"QuoteResultPage__SliderSection__SubSection"}>
            <div className={"QuoteResultPage__SliderSection__Label"}>How long should your coverage last?</div>
            <div className={"QuoteResultPage__SliderSection__Value"}>{TERM_CHOICES[termHover || term]}</div>
            <Slider
                className={"QuoteResultPage__SliderSection__Slider"}
                showMarks
                values={TERM_VALUES}
                defaultValue={term}
                onChange={term => setTermHover(term)}
                onAfterChange={term => {
                    setTermHover(term);
                    setQuoteInput({term});
                }}
            />

            <div className={"QuoteResultPage__SliderSection__SubSection__Separator"}/>

            <div className={"QuoteResultPage__SliderSection__Label"}>How often would you like to pay?</div>
            <RadioButtonList
                className={"QuoteResultPage__SliderSection__RadioButtons"}
                onChange={value => setQuoteInput({payment: value})}
                options={paymentOptions}
                value={payment}
            />
        </div>
    </div>;
}

function ageFromDob(dob) {
    if (!dob) return;
    const thisYearBirthday = dayjs(dob).year(dayjs().year());
    return dayjs().year() - dayjs(dob).year() + (dayjs() > thisYearBirthday ? 1 : 0);
}

export default QuoteResultPage;
